import { Typography } from '@interstate/components/Typography';
import { Button } from '@interstate/components/Button';
import { CardHeader, ButtonGroup } from './CustomerAddresses.styles';
import { useCustomerAddress } from './CustomerAddressProvider';
import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import { DocumentDuplicateIcon } from '@interstate/components/Icons/DocumentDuplicateIcon';
import { ArrowsUpDownIcon } from '@interstate/components/Icons/ArrowsUpDownIcon';
import { PencilSquareIcon } from '@interstate/components/Icons/PencilSquareIcon';
import type { AddressType } from './CustomerAddressTypes';

type AddressCardHeaderProps = {
    type: AddressType;
};

export const AddressCardHeader = ({ type }: AddressCardHeaderProps) => {
    const {
        handleEditAddress,
        getAddressByType,
        handleCopyToBilling,
        handleDeleteAddress,
        handleSwapAddresses,
        addresses,
        disabled,
        hasAddressErrors,
        hasPrimaryAddressErrors
    } = useCustomerAddress();

    const { title, mode } = getAddressByType(type);

    const renderCommonButtons = () => {
        return (
            <>
                {type === 'billing' ? (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        onClick={() => handleDeleteAddress(type)}
                        data-testid={`address-card-delete-${type}`}
                        disabled={disabled}
                    >
                        Delete
                        <TrashIcon data-testid={`address-card-delete-icon-${type}`} />
                    </Button>
                ) : (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        onClick={handleCopyToBilling}
                        disabled={disabled || hasPrimaryAddressErrors()}
                        data-testid={`address-form-copy-to-billing-${type}`}
                    >
                        Copy to Billing
                        <DocumentDuplicateIcon data-testid={`address-form-copy-to-billing-icon-${type}`} />
                    </Button>
                )}
                {addresses.length > 1 && (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        onClick={handleSwapAddresses}
                        disabled={disabled || hasAddressErrors()}
                        data-testid={`address-card-swap-${type}`}
                    >
                        Swap
                        <ArrowsUpDownIcon data-testid={`address-card-swap-icon-${type}`} />
                    </Button>
                )}
            </>
        );
    };

    const renderViewModeButtons = () => (
        <>
            {renderCommonButtons()}

            <Button
                buttonStyle="tertiary"
                size="small"
                onClick={() => handleEditAddress(type)}
                data-testid={`address-card-edit-${type}`}
                disabled={disabled}
            >
                Edit
                <PencilSquareIcon data-testid={`address-card-edit-icon-${type}`} />
            </Button>
        </>
    );

    return (
        <CardHeader>
            <Typography variant="h6">{title}</Typography>
            <ButtonGroup>{mode === 'view' ? renderViewModeButtons() : renderCommonButtons()}</ButtonGroup>
        </CardHeader>
    );
};
